import { useSupportedSources } from '@pages/Transpiler/useSupportedSources';
import { theme } from '@prophecy/theme';
import {
  Dialog as UIDialog,
  OverlaySpinner,
  RadioButtonGroup,
  Stack,
  StackItem,
  StyledRadioGroupItem,
  Text,
  RadioButtonIconContainerClassName
} from '@prophecy/ui-v3';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useEnabledTranspilers } from '../../../Team/hooks/useEnabledTranspilers';
import { EnabledTranspilerTypeOptions, FileFormatLabels, FileFormats } from '../../config';
import { ResetState } from '../types';
import { useImportState } from '../useImportState';
import { StyledStackContainer, StyledWhiteCard } from './styled';

const StyledRadioButtonGroup = styled(RadioButtonGroup)`
  padding: ${theme.spaces.x4};
  &&& ${StyledRadioGroupItem} {
    height: auto;
    padding: ${theme.spaces.x24};
    background: inherit;
  }

  ${RadioButtonIconContainerClassName} {
    svg {
      width: 70px;
      margin-right: ${theme.spaces.x12};
    }
  }
`;
export const ImportSources = () => {
  const {
    actions: { handleTranspilerTypeChange },
    transpileGraphState: {
      actions: { resetTranspilationState },
      clientState: {
        resetState: { resetType }
      }
    },
    transpilerState: {
      transpilerId,
      transpilerEntityTypes: { pipelines, jobs, datasets = [] },
      uploadedFilePaths
    },
    formDataWithActions: { formContext }
  } = useImportState();
  const { watch } = formContext;

  const transpilerType = watch('source');
  const [source, setSourceType] = useState(transpilerType);
  const enabledTranspilers = useEnabledTranspilers();
  useEffect(() => {
    //UI waiting for reset state notification failed/succeed, so ui will wait for notification from backend
    if (resetType !== ResetState.initial) {
      resetTranspilationState(true);
    }
  }, [resetType, source, transpilerType, handleTranspilerTypeChange, resetTranspilationState]);

  const hasDatatoReset = jobs.length > 0 || pipelines.length > 0 || uploadedFilePaths.length > 0 || datasets.length > 0;
  const handleOnChange = async (currentSource: string) => {
    if (transpilerId && transpilerType !== '') {
      //Show wanring if some data is set for transpiler type
      if (hasDatatoReset) {
        const allowedDialogResult = await UIDialog.warning({
          title: `Modify Source Type?`,
          children: (
            <Text level='sm'>
              Please note that all the information of uploaded and transpiled files for{' '}
              <b>{FileFormatLabels[transpilerType as FileFormats]}</b> source type will be lost from the bundle as you
              are modifying the source type to <b>{FileFormatLabels[currentSource as FileFormats]}</b>. Do you want to
              continue?
            </Text>
          ),
          'data-test-id': 'modify-source-type-dialog'
        });
        if (allowedDialogResult !== UIDialog.ResponseAction.okButton) {
          return;
        }
      }

      resetTranspilationState(false, currentSource);
    } else {
      handleTranspilerTypeChange(currentSource as FileFormats);
    }
    setSourceType(currentSource);
  };

  const supportedSources = useSupportedSources();

  const enabledTranspileTypeOptions = useMemo(() => {
    return EnabledTranspilerTypeOptions.map((option) => {
      const hasTypeCheckFound = enabledTranspilers[option.value] !== undefined;
      const typeDisabled = hasTypeCheckFound ? !enabledTranspilers[option.value] : option.disabled;
      return {
        ...option,
        disabled: typeDisabled,
        tooltip: typeDisabled ? 'This imported source type is not enabled for your team' : undefined
      };
    }).filter((op) => !op.disabled && supportedSources.includes(op.value));
  }, [enabledTranspilers, supportedSources]);

  return (
    <StyledWhiteCard>
      {resetType === ResetState.started && <OverlaySpinner spinning={true} />}
      <Stack gap={theme.spaces.x16} grow='1'>
        <Text level='xl' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
          1. Select Source Type
        </Text>
        <Text level='sm' tone={theme.colors.gray700}>
          Select the Legacy ETL system you want to import into Prophecy:
        </Text>

        <StackItem grow='1'>
          <Stack gap={theme.spaces.x12} height='100%' padding={`${theme.spaces.x16} ${theme.spaces.x6} `}>
            <Text level='sm' weight={theme.fontWeight.medium} tone={theme.colors.gray700}>
              Supported Formats
            </Text>
            <StyledStackContainer gap={theme.spaces.x12} grow='1'>
              <StyledRadioButtonGroup
                options={enabledTranspileTypeOptions}
                value={transpilerType}
                onChange={handleOnChange}
              />
            </StyledStackContainer>
          </Stack>
        </StackItem>
      </Stack>
    </StyledWhiteCard>
  );
};
